/**
 * A failure of the *request*, as opposed to an answer that came back negative.
 *
 * The distinction is the whole reason this type exists, and it is the one every
 * card on this screen has to get right. A credential SES rejected, a CNAME that
 * has not propagated, a tracking change the API refused — those are **answers**,
 * and each is rendered in its own place with its own repair. This is the other
 * thing: Wend not having got an answer at all. Showing the two the same way
 * teaches a person that the red block on this screen means nothing in
 * particular.
 *
 * `retryAfterSeconds` is carried because every route on this screen that
 * touches a third party on the tenant's own account is rate limited — the SES
 * check, the DNS check, provisioning — and "too many attempts" without a number
 * is an invitation to keep pressing the button that is failing.
 *
 * **Why this is a file now.** It was written twice, identically, in
 * `IntegrationsPage.tsx` and `WhatsAppCard.tsx`. Slice 11 adds a third card to
 * the same screen with three more buttons that can fail this way, and a third
 * verbatim copy of a twenty-line block is where a fix starts landing in one
 * copy and not the others. The alternative considered was leaving the two
 * copies alone and writing a third beside them, on the grounds that the
 * duplication was already tolerated — rejected, because "already tolerated" is
 * an argument that never expires and this is the point at which it stops being
 * cheap. Nothing about the behaviour changed in the move; both original copies
 * were byte-identical to what is here.
 */
import { FormAlert } from '../../components/FormAlert';
import { errorMessage, isApiError } from '../../lib/api/errors';
import { pluralise } from '../../lib/format';

export interface RequestFailure {
  readonly message: string;
  readonly requestId: string | undefined;
  readonly retryAfterSeconds: number | undefined;
}

export function requestFailure(error: unknown): RequestFailure {
  return {
    message: errorMessage(error),
    requestId: isApiError(error) ? error.requestId : undefined,
    retryAfterSeconds: isApiError(error) ? error.retryAfterSeconds : undefined,
  };
}

export function RequestFailureAlert({ failure }: { failure: RequestFailure }) {
  return (
    <FormAlert
      tone="error"
      {...(failure.requestId === undefined ? {} : { requestId: failure.requestId })}
    >
      {failure.message}
      {failure.retryAfterSeconds !== undefined && (
        <> Try again in {pluralise(failure.retryAfterSeconds, 'second')}.</>
      )}
    </FormAlert>
  );
}
